<template>
  <header>
    <h2>Kes Eesti kodude tolmus elavad?</h2>
    <p>
      Tartu linna ja Elva valla kodudest kogutud tolmuproovidest leitud
      pärilikkusainest määratud erinevatest selgrootute seltsidesse kuuluvate
      isendite osakaalud.
    </p>
  </header>

  <main>
    <section class="scale">
      <aside>Esinemissagedus kodudes (%)</aside>
      <div>20</div>
      <div style="top: 25%">15</div>
      <div style="top: 50%">10</div>
      <div style="top: 75%">5</div>
      <div style="top: 100%">0</div>
    </section>

    <section class="bars">
      <div
        v-for="({ selts, esinemissagedus, pilt, suurus }, index) in graphicData"
        :id="`item-value-${index}`"
        class="item-value"
        :style="{ height: `${(esinemissagedus / maxValue) * 100}%` }"
      >
        <div
          class="image"
          :style="{
            transform: `translateY(calc(-120% - ${
              suurus * suurus * 15
            }%)) scale(${suurus})`,
          }"
        >
          <img :src="`bugs/${pilt}`" />
        </div>

        <div class="bar"></div>

        <span> {{ esinemissagedus }} % </span>
      </div>

      <div class="item-label" v-for="{ selts, seltsLd } in graphicData">
        <span>{{ selts }}</span>

        <small>{{ seltsLd }}</small>
      </div>
    </section>
  </main>
</template>

<style>
#widget {
  --section-color: #009ee2;
  --section-secondary-color: #052862;
  --dark-color: #333;

  width: 100%;
  height: 100%;
  margin: auto;

  max-height: 720px;

  font-family: Roboto, Arial, sans-serif;
  font-size: 16px;
  word-spacing: 0 !important;
  letter-spacing: 0;
  line-height: 1;
  color: white;
  background-color: var(--dark-color);

  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  overflow: hidden;
  position: relative;
}
</style>

<style scoped>
header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;

  display: grid;
  place-items: center;

  box-sizing: border-box;
  padding: 1em;

  text-align: center;

  z-index: 2;
}

header p {
  margin: 0;
  width: 30em;
  max-width: 62vw; /* mobile */
  line-height: 1.25;
}

main {
  --bars-count: v-bind(dataLength);
  --bar-max-h: v-bind(barMaxHeight + "px");

  display: grid;
  grid-template-columns: auto 1fr;

  box-sizing: border-box;
  padding: calc(0.25 * var(--bar-max-h)) 1em 0.5em;

  width: 100%;
  max-width: 720px;

  margin: 0 auto;
}

.scale {
  width: 4em;
  height: var(--bar-max-h);

  position: relative;

  white-space: nowrap;
  overflow: visible;

  user-select: none;
  pointer-events: none;

  opacity: 0.75;

  z-index: 2;
}

.scale aside {
  font-weight: bold;
  position: absolute;
  top: 50%;
  left: 0;
  transform-origin: left;
  transform: rotate(-90deg) translateX(-50%) translateY(50%);

  /*font-size: 0.75em;*/
  font-variant: small-caps;
  letter-spacing: 0.025em;
}

.scale div {
  position: absolute;
  left: 100%;
  transform: translate(-125%, -50%);

  font-size: 0.75em;
  font-weight: bold;
}

.bars {
  display: grid;
  grid-template-columns: repeat(var(--bars-count), minmax(0, 1fr));
  grid-template-rows: var(--bar-max-h) 1fr;
  gap: 0.25em 0;
}

.item-value {
  align-self: end;
  justify-self: center;

  width: 100%;

  display: flex;
  align-items: flex-start;
  justify-content: center;

  border-bottom: 1px solid rgba(255 255 255 / 0.75);

  position: relative;
}

.item-value span {
  color: var(--section-color);

  font-size: clamp(0.5em, 2.5vw, 1em);

  transform: translateY(-1.5em);
  white-space: nowrap;
  font-weight: bold;

  text-shadow: -1px -1px 0 var(--dark-color), 1px -1px 0 var(--dark-color),
    -1px 1px 0 var(--dark-color), 1px 1px 0 var(--dark-color);

  z-index: 1;
}

.bar {
  position: absolute;

  width: 50%;
  height: 100%;

  background-color: var(--section-color);
  /*opacity: 0.75;*/

  z-index: 1;
}

.item-value span,
.item-value .bar {
  filter: brightness(1.2);
}
.item-value:hover span,
.item-value:hover .bar {
  filter: brightness(1.45);
  z-index: 2;
}

.image {
  position: absolute;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  pointer-events: none;
  user-select: none;

  transform-origin: center;

  z-index: 0;

  mix-blend-mode: lighten;
}

.image img {
  width: 100%;
  filter: brightness(0.95) contrast(1);
  transition: 90ms ease;
}

.item-value:hover .image img {
  filter: brightness(1.5) contrast(1.2);
}

.item-label {
  writing-mode: vertical-rl;
  text-orientation: mixed;

  letter-spacing: 0.025em;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;

  gap: 2px;

  transform-origin: center;
  transform: scaleX(-1) scaleY(-1);
}

.item-label small {
  font-size: clamp(0.35em, 1.5vw, 0.75em);
  font-style: italic;
  opacity: 0.75;
}

@media (max-width: 545px) {
  .scale {
    width: 2em;
  }

  .scale aside {
    display: none;
  }

  .item-label {
    font-size: 0.75em;
  }

  .item-label small {
    display: none;
  }

  main {
    padding: 1em;
  }

  header {
    /*position: unset;*/
    /*margin-top: auto;*/
  }
  /*header p {
    font-size: 0.75em;
    line-height: 1.1;
    margin: 0;
  }*/
}
</style>

<script>
import tippy, { followCursor } from "tippy.js";

export default {
  name: "Widget",
  data: () => ({
    containerElement: null,
    // isLarge: true,

    graphicData: [
      {
        seltsLd: "Entomobryomorpha",
        selts: "Hooghännalised",
        esinemissagedus: 18.8,
        pilt: "Entomobryomorpha.jpg",
        suurus: 1,
      },
      {
        seltsLd: "Diptera",
        selts: "Kahetiivalised",
        esinemissagedus: 11.9,
        pilt: "k2rbes2.jpg",
        suurus: 2.8,
      },
      {
        seltsLd: "Hemiptera",
        selts: "Nokalised",
        esinemissagedus: 11.6,
        pilt: "Hemptera.jpg",
        suurus: 1.1,
      },
      {
        seltsLd: "Coleoptera",
        selts: "Mardikalised",
        esinemissagedus: 10.7,
        pilt: "Coleoptera.jpg",
        suurus: 1.8,
      },
      {
        seltsLd: "Psocoptera",
        selts: "Kõdutäilised",
        esinemissagedus: 7.5,
        pilt: "Psocoptera.jpg",
        suurus: 1.6,
      },
      {
        seltsLd: "Sarcoptiformes",
        selts: "Närilestalised",
        esinemissagedus: 7.0,
        pilt: "Sarcoptiformes.jpg",
        suurus: 1.3,
      },
      {
        seltsLd: "Lepidoptera",
        selts: "Liblikalised",
        esinemissagedus: 3.2,
        pilt: "Lepidoptera.jpg",
        suurus: 6.4,
      },
      {
        seltsLd: "Hymenoptera",
        selts: "Kiletiivalised",
        esinemissagedus: 2.5,
        pilt: "Hymenoptera.jpg",
        suurus: 1,
      },
      {
        seltsLd: "Araneae",
        selts: "Ämblikulised",
        esinemissagedus: 1.4,
        pilt: "Araneae.jpg",
        suurus: 3.6,
      },
      {
        seltsLd: "Symphypleona",
        selts: "Hooghännalised",
        esinemissagedus: 1.4,
        pilt: "Symphypleona.png",
        suurus: 1.5,
      },
    ],

    barMaxHeight: 0,
  }),
  mounted() {
    this.containerElement = document.querySelector("#widget");
    // console.log("Widget mounted", this.containerElement, window);

    this.graphicData.forEach((row, i) => {
      tippy(`#item-value-${i}`, {
        theme: "light",
        allowHTML: true,
        content: `<span>${row.selts}</span>
          <br/>
          <em>${row.seltsLd}</em>
          <br/>
          <b>${row.esinemissagedus} %</b>`,
        arrow: false,
        followCursor: true,
        plugins: [followCursor],
        placement: "bottom-end",
      });
    });

    window.addEventListener("resize", this.onResize);
    this.onResize();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    onResize() {
      const bounds = this.containerElement.getBoundingClientRect();
      // console.log("Widget bounds", bounds);
      const { width, height } = bounds;
      // console.log(width, height);
      // this.isLarge = width > 600;
      this.barMaxHeight = 0.44 * height;
    },
  },
  computed: {
    dataLength() {
      return this.graphicData.length;
    },
    maxValue() {
      // const sagedused = this.graphicData.map((x) => x.esinemissagedus);
      // return Math.max(...sagedused);
      return 20;
    },
  },
};
</script>
